// src/component/ui/Skeletons.jsx

import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Skeleton from '@mui/material/Skeleton';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

const ShimmerCard = styled(Card)(({ theme }) => ({
  borderRadius: '20px',
  padding: '24px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  borderColor: theme.palette.divider,
  boxShadow: 'none',
}));

/**
 * SkeletonCard - Renders a shimmering grid card placeholder matching PremiumCard shape
 */
export const SkeletonCard = () => {
  return (
    <ShimmerCard variant="outlined">
      <Box>
        {/* Top badge or lock spacer */}
        <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="rectangular" width={70} height={20} style={{ borderRadius: '8px' }} />
        </Box>
        {/* Title */}
        <Skeleton variant="text" width="80%" height={28} style={{ marginBottom: '12px' }} />
        {/* Subtitle */}
        <Skeleton variant="text" width="60%" height={20} style={{ marginBottom: '8px' }} />
        <Skeleton variant="text" width="40%" height={20} style={{ marginBottom: '24px' }} />
      </Box>
      <Box>
        {/* Bottom progress bar placeholder */}
        <Skeleton variant="text" width="30%" height={16} style={{ marginBottom: '8px' }} />
        <Skeleton variant="rectangular" width="100%" height={6} style={{ borderRadius: '3px' }} />
      </Box>
    </ShimmerCard>
  );
};

/**
 * SkeletonExam - Renders a mock layout of the MCQ exam interface
 */
export const SkeletonExam = () => {
  return (
    <Box style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
      {/* Left panel: Question content */}
      <Box style={{ flex: 3, minWidth: '300px' }}>
        <Card variant="outlined" style={{ padding: '32px', borderRadius: '20px', marginBottom: '24px' }}>
          <Skeleton variant="text" width="30%" height={20} style={{ marginBottom: '20px' }} />
          <Skeleton variant="text" width="95%" height={32} style={{ marginBottom: '12px' }} />
          <Skeleton variant="text" width="85%" height={32} style={{ marginBottom: '32px' }} />
          
          {/* Options */}
          {[1, 2, 3, 4].map((i) => (
            <Skeleton 
              key={i} 
              variant="rectangular" 
              width="100%" 
              height={56} 
              style={{ borderRadius: '16px', marginBottom: '16px' }} 
            />
          ))}
        </Card>
      </Box>
      
      {/* Right panel: Sticky timer and palette */}
      <Box style={{ flex: 1, minWidth: '260px' }}>
        <Card variant="outlined" style={{ padding: '24px', borderRadius: '20px' }}>
          <Skeleton variant="rectangular" width="100%" height={48} style={{ borderRadius: '16px', marginBottom: '24px' }} />
          <Skeleton variant="text" width="60%" height={20} style={{ marginBottom: '16px' }} />
          
          {/* Palette circle grid */}
          <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' }}>
            {Array.from({ length: 15 }).map((_, idx) => (
              <Skeleton key={idx} variant="circular" width={36} height={36} />
            ))}
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

/**
 * LoadingOverlay - Backdrop blurred loading overlay for API submissions
 * @param {Object} props
 * @param {boolean} props.open - Toggle active overlay state
 * @param {string} [props.message="Processing..."] - supporting descriptive label
 */
export const LoadingOverlay = ({ open, message = 'Processing...' }) => {
  return (
    <Backdrop
      sx={{ 
        color: '#FFFFFF', 
        zIndex: (theme) => theme.zIndex.drawer + 500,
        backdropFilter: 'blur(8px)',
        backgroundColor: 'rgba(15, 23, 42, 0.4)',
      }}
      open={open}
    >
      <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
        <CircularProgress color="inherit" size={48} />
        <Typography variant="subtitle1" style={{ color: '#FFFFFF', fontWeight: 600 }}>
          {message}
        </Typography>
      </Box>
    </Backdrop>
  );
};


